import React, { useEffect, useState } from 'react';
import Papa from 'papaparse';
import { useLocation } from 'react-router-dom';
import './Speakers.css';
import { toSpeakerSlug } from '../../utils/speakerLinks';

const panelOrder = [
    'Welcome',
    'Panel 1',
    'Panel 2',
    'Fireside Chat',
    'Panel 3',
    'Panel 4',
    'Closing remarks',
];

const panelDisplayNames = {
    'Panel 1': 'Panel 1: Outlook: AI and Digital Infrastructure Policy and Strategy',
    'Panel 2': 'Panel 2: AI and Digital Infrastructure Emerging Technologies',
    'Panel 3': 'Panel 3: AI and Digital Infrastructure Deployment Opportunities',
    'Panel 4': 'Panel 4: Research and Innovation in AI and Transportation',
};

const speakerOrderByPanel = {
    'Fireside Chat': [
        'Anthony Perez',
        'Alain Kornhauser',
        'Sam Schwartz',
        'Karl Wunderlich',
        'Georges Aoude',
        'Abraham Emmanuel',
        'Clement Lau',
    ],
};

const Speakers = () => {
    const [speakers, setSpeakers] = useState([]);
    const [expandedBios, setExpandedBios] = useState({});
    const location = useLocation();
    const selectedSpeaker = new URLSearchParams(location.search).get('person');

    useEffect(() => {
        Papa.parse(`${process.env.PUBLIC_URL}/speakers.csv`, {
            download: true,
            header: true,
            skipEmptyLines: true,
            complete: (result) => {
                setSpeakers(result.data.filter((speaker) => speaker['Speaker Name']));
            },
        });
    }, []);

    useEffect(() => {
        if (!speakers.length || !selectedSpeaker) return;

        const profile = document.getElementById(selectedSpeaker);
        if (profile) {
            profile.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [selectedSpeaker, speakers]);

    const toggleBio = (slug) => {
        setExpandedBios((current) => ({
            ...current,
            [slug]: !current[slug],
        }));
    };

    return (
        <section id="speakers" className="speakers-section">
            <h2 className="info-title">Speakers &amp; Moderators</h2>
            <p className="speakers-intro">Speaker biographies by program session</p>

            {panelOrder.map((panel) => {
                const panelSpeakers = speakers.filter((speaker) =>
                    (speaker.Panel || '')
                        .split(';')
                        .map((value) => value.trim())
                        .filter(Boolean)
                        .includes(panel)
                );
                const preferredOrder = speakerOrderByPanel[panel];
                if (preferredOrder) {
                    panelSpeakers.sort((first, second) =>
                        preferredOrder.indexOf(first['Speaker Name'])
                        - preferredOrder.indexOf(second['Speaker Name'])
                    );
                }
                if (!panelSpeakers.length) return null;

                return (
                    <section className="speaker-panel" key={panel} aria-labelledby={`panel-${toSpeakerSlug(panel)}`}>
                        <h3 className="speaker-panel-title" id={`panel-${toSpeakerSlug(panel)}`}>
                            {panelDisplayNames[panel] || panel}
                        </h3>
                        <ul className="speaker-list">
                            {panelSpeakers.map((speaker) => {
                                const name = speaker['Speaker Name'];
                                const displayName = speaker.Credentials
                                    ? `${name}, ${speaker.Credentials}`
                                    : name;
                                const slug = toSpeakerSlug(name);
                                const speakerPanels = (speaker.Panel || '')
                                    .split(';')
                                    .map((value) => value.trim())
                                    .filter(Boolean);
                                const profileId = speakerPanels[0] === panel
                                    ? slug
                                    : `${slug}-${toSpeakerSlug(panel)}`;
                                const image = `${process.env.PUBLIC_URL}/headshots/${speaker.Image}`;
                                const isExpanded = Boolean(expandedBios[profileId]);

                                return (
                                    <li
                                        className={`speaker-profile ${selectedSpeaker === profileId ? 'speaker-profile-targeted' : ''}`}
                                        id={profileId}
                                        key={profileId}
                                    >
                                        <img
                                            className="speaker-image"
                                            src={image}
                                            alt={name}
                                            loading="lazy"
                                            decoding="async"
                                        />
                                        <div className="speaker-content">
                                            <h4>{displayName}</h4>
                                            <p className="speaker-affiliation">{speaker.Affiliation}</p>
                                            {speaker.Bio ? (
                                                <>
                                                    <p
                                                        className={`speaker-bio ${isExpanded ? 'speaker-bio-expanded' : ''}`}
                                                        id={`bio-${profileId}`}
                                                    >
                                                        {speaker.Bio}
                                                    </p>
                                                    <button
                                                        type="button"
                                                        className="speaker-bio-toggle"
                                                        aria-expanded={isExpanded}
                                                        aria-controls={`bio-${profileId}`}
                                                        aria-label={`${isExpanded ? 'Show less about' : 'Read more about'} ${name}`}
                                                        onClick={() => toggleBio(profileId)}
                                                    >
                                                        {isExpanded ? 'Show less' : 'Read more'}
                                                    </button>
                                                </>
                                            ) : null}
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    </section>
                );
            })}
        </section>
    );
};

export default Speakers;
